// Stage 33: Bulletin Canvas — body renderer (sandboxed iframe host).
//
// Loads the latest body from the worker, wraps it in a strict CSP, and
// passes it through `srcdoc` to a sandboxed iframe. The sandbox attribute
// uses ONLY `allow-scripts` (no allow-same-origin), so:
//
//   - Embedded scripts can run inside the iframe.
//   - The iframe has an opaque origin → cannot read parent cookies/storage.
//   - The CSP `connect-src 'none'` blocks fetch/XHR/WebSocket from the
//     iframe code.
//   - `img-src data:` blocks tracking pixels.
//
// For markdown panels the body is rendered to HTML by the local
// `renderMarkdownToHtml` (no third-party deps). Same wrapping applies.

import { useEffect, useState } from 'react';
import { getPanelRender } from './bulletinClient';
import { renderMarkdownToHtml } from './markdownRender';
import type { BulletinPanelDTO } from './types';

// Stage 36: allow images served by our own worker (used by
// prepare_image_for_panel's image_url). connect-src stays 'none'
// — agent code still cannot fetch. The deployed worker origin is
// inlined at build time from VITE_API_BASE.
const API_BASE = import.meta.env.VITE_API_BASE as string;
const CSP_META =
  '<meta http-equiv="Content-Security-Policy" ' +
  'content="default-src \'self\' \'unsafe-inline\' data:; ' +
  'script-src \'unsafe-inline\'; ' +
  'connect-src \'none\'; ' +
  `img-src data: ${API_BASE}; ` +
  'style-src \'unsafe-inline\';">';

const BASE_STYLE =
  '<style>html,body{margin:0;padding:0;font-family:system-ui,sans-serif;' +
  'color:#111;background:transparent;}body{padding:12px 14px;line-height:1.45;}' +
  'pre{background:#0f172a;color:#e2e8f0;padding:10px;border-radius:6px;' +
  'overflow:auto;font-family:ui-monospace,monospace;}code{font-family:' +
  'ui-monospace,monospace;}h1,h2,h3,h4{margin:0.6em 0 0.3em;}p{margin:0.4em 0;}' +
  'blockquote{margin:0.4em 0;padding:0 10px;border-left:3px solid #94a3b8;' +
  'color:#475569;}img{max-width:100%;}</style>';

function wrapWithCSP(body: string): string {
  // Stage 29: yesterday output is always rendered through the markdown
  // pipeline, so the body is plain HTML inside our scaffold. Keep the
  // <head> / <html> branches for forward-compatibility if a future
  // stage re-introduces full-document panel content.
  if (/<head[^>]*>/i.test(body)) {
    return body.replace(/<head([^>]*)>/i, `<head$1>${CSP_META}`);
  }
  if (/<html[\s>]/i.test(body)) {
    return body.replace(/<html([^>]*)>/i, `<html$1><head>${CSP_META}</head>`);
  }
  return `<!DOCTYPE html><html><head>${CSP_META}${BASE_STYLE}</head><body>${body}</body></html>`;
}

// Stage 29: yesterday output format is fixed (defined in
// worker/src/scheduled.ts):
//   ---<yaml frontmatter>---
//   # <task name>
//   **Executed:** <iso>
//   **Koji:** <koji>
//   ## Prompt
//   <prompt body>
//   ## Response
//   <response body>
//
// The panel renders only the Response section so prompt + frontmatter
// scaffolding stays out of the visible surface. If the marker is
// missing (malformed run), we fall back to the full body so the user
// sees something rather than an empty panel.
function extractResponse(yesterdayMd: string): string {
  const marker = /^##\s+Response\s*$/m;
  const match = marker.exec(yesterdayMd);
  if (!match) return yesterdayMd;
  return yesterdayMd.slice(match.index + match[0].length).trim();
}

export function BulletinPanelBody({
  panel,
}: {
  panel: BulletinPanelDTO;
}): JSX.Element {
  const [srcdoc, setSrcdoc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Stage 33: render endpoint serves pinnedR2Key when set, else
    // latestR2Key. Either being null means there's nothing to show.
    const effectiveKey = panel.pinnedR2Key ?? panel.latestR2Key;
    if (!effectiveKey) {
      setSrcdoc(null);
      return;
    }
    let alive = true;
    setLoading(true);
    void getPanelRender(panel.id)
      .then((res) => {
        if (!alive) return;
        if (res.format === 'html') {
          // Stage 30: write_bulletin_html output. Body is a complete
          // HTML document; wrap with the same CSP meta tag and render
          // verbatim. The sandboxed iframe + meta-CSP combination is
          // the trust boundary regardless of source.
          setSrcdoc(wrapWithCSP(res.body));
        } else {
          // Stage 29 default: markdown response. Extract the Response
          // section so prompt + frontmatter scaffolding don't leak
          // into the visible panel; render through the markdown→HTML
          // pipeline.
          const responseMd = extractResponse(res.body);
          const html = renderMarkdownToHtml(responseMd);
          setSrcdoc(wrapWithCSP(html));
        }
      })
      .catch(() => {
        if (alive) setSrcdoc(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // Re-fetch when last_run_at changes (writer overwrote latest.{ext})
    // OR when pinnedR2Key changes (Stage 33: pin / unpin / change).
  }, [panel.id, panel.pinnedR2Key, panel.latestR2Key, panel.lastRunAt]);

  if (panel.status === 'pending') {
    return (
      <div className="bulletin-placeholder">
        bind a task to fill this panel
      </div>
    );
  }
  if (panel.status === 'linked' && !srcdoc) {
    return (
      <div className="bulletin-placeholder">
        scheduled — awaiting first run
      </div>
    );
  }
  if (panel.status === 'error') {
    return (
      <div className="bulletin-placeholder bulletin-placeholder-error">
        <div>⚠ {panel.lastError ?? 'task failed'}</div>
        {srcdoc ? (
          <iframe
            sandbox="allow-scripts"
            srcDoc={srcdoc}
            title={panel.koji}
            className="bulletin-iframe"
          />
        ) : null}
      </div>
    );
  }
  if (loading && !srcdoc) {
    return <div className="bulletin-placeholder">loading…</div>;
  }
  if (!srcdoc) {
    return <div className="bulletin-placeholder">no output</div>;
  }
  return (
    <iframe
      sandbox="allow-scripts"
      srcDoc={srcdoc}
      title={panel.koji}
      className="bulletin-iframe"
    />
  );
}

